// Register — /register. Sign up for TMCChat without a TMCHub account first: creates a PENDING TMCHub
// account; staff approve it in Dream Team (pick client + products), then an email signs them in.
// Lists only the default client's (Affinity's) products as tick boxes; ?c=… picks another configured client.

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AuthApi } from "../hooks/useApi.js";
import { LoginShell } from "./Login.jsx";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", company: "", note: "", password: "", confirm: "", website: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const code = useSearchParams()[0].get("c") || "";
  const [offer, setOffer] = useState(null); // { clientName, products } for a client's link
  const [picked, setPicked] = useState([]);
  useEffect(() => {
    AuthApi.signupOptions(code).then(setOffer).catch(() => setOffer(null)); // no code = the default client (Affinity)
  }, [code]);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const cyber = typeof document !== "undefined" && document.documentElement.dataset.theme === "cyberpunk";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters long."); return; }
    if (form.password !== form.confirm) { setError("The two passwords don't match."); return; }
    if (offer && !picked.length) { setError("Tick the apps you need access to."); return; }
    setBusy(true);
    try {
      await AuthApi.register({ name: form.name, email: form.email, company: form.company, note: form.note,
                               password: form.password, website: form.website,
                               code: offer ? code : undefined, productIds: offer ? picked : undefined });
      setDone(true);
    } catch (err) {
      setError(err.body?.error || "Could not create your account. Try again shortly.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <LoginShell onSubmit={submit} cyber={cyber}>
      <p className="login__tagline">Request access to TMCChat{offer?.clientName ? ` · ${offer.clientName}` : ""}</p>
      {done ? (
        <div className="login__notice">
          Thanks, <b>{form.name.split(" ")[0]}</b>! The TMC team will set up your access. You'll get an email at <b>{form.email}</b> with
          a link that signs you straight in.
        </div>
      ) : (
        <>
          <div className="field">
            <label className="field__label">Full name</label>
            <input className="input" autoComplete="name" value={form.name} onChange={set("name")} required maxLength={200} autoFocus />
          </div>
          <div className="field">
            <label className="field__label">Work email</label>
            <input className="input" type="email" autoComplete="username" value={form.email} onChange={set("email")} required maxLength={320} />
          </div>
          {/* A client's link already says who they are — only the plain form asks. */}
          {!offer && (
            <div className="field">
              <label className="field__label">Company</label>
              <input className="input" autoComplete="organization" value={form.company} onChange={set("company")} maxLength={200}
                     placeholder="Who you work for" />
            </div>
          )}
          {offer ? (
            <div className="field">
              <label className="field__label">Which apps do you need access to?</label>
              <div className="register__apps">
                {offer.products.map((p) => (
                  <label key={p.productId} className="register__app">
                    <input type="checkbox" checked={picked.includes(p.productId)} onChange={() => toggle(p.productId)} />
                    <span>{p.name}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="field">
              <label className="field__label">Which app or project? <span className="login__opt">(optional)</span></label>
              <input className="input" value={form.note} onChange={set("note")} maxLength={1000} placeholder="e.g. Virtual GP" />
            </div>
          )}
          <div className="field">
            <label className="field__label">Password</label>
            <input className="input" type="password" autoComplete="new-password" value={form.password} onChange={set("password")} required minLength={6} />
          </div>
          <div className="field">
            <label className="field__label">Confirm password</label>
            <input className="input" type="password" autoComplete="new-password" value={form.confirm} onChange={set("confirm")} required minLength={6} />
          </div>
          {/* Honeypot: hidden from people, bots fill it in. */}
          <input className="login__hp" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} aria-hidden />
          {error && <div className="field__err">⚠ {error}</div>}
          <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
            {busy ? <span className="btn__spin" /> : "Request access"}
          </button>
        </>
      )}
      <p className="login__alt">Already have an account? <Link to="/login">Sign in</Link></p>
    </LoginShell>
  );
}
